import Head from "next/head";
import { useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { withEmotionCache } from "@emotion/react";
import { useConfiguration } from "../dataAccess/api/configuration";
import { useLocale } from "../helpers/locale";
import useFontsLayout from "./useFontsLayout";
import { getIconsUrl } from "./icons";
import { useApiClientContext } from "../app/ApiClientCtx";

interface Props {
  titleSufix?: string;
}

const CommonHead = withEmotionCache(({ titleSufix }: Props, cache) => {
  const { appFontsTag, googleFontsTag } = useFontsLayout(cache.nonce || "");
  const theme = useTheme();
  const { configuration } = useConfiguration();
  const { locale: currentLocale } = useLocale();
  const { asPath } = useRouter();
  const [hiddenIcons, setHiddenIcons] = useState(true);
  const { APIV3 } = useApiClientContext();

  useEffect(() => {
    if (theme.settings.iconSet) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = getIconsUrl(theme.settings.iconSet);
      document.head.appendChild(link);
      setHiddenIcons(false);
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [theme.settings.iconSet]);

  const titlePrefixContent = `${
    theme.settings?.pagesTitlePrefix || configuration?.name
  }`;
  const titleSufixContent = titleSufix ? ` | ${titleSufix}` : "";

  const domain =
    configuration?.customDomain ||
    `https://${configuration?.subdomain}.builtfirst.com/`;
  const name = configuration?.name;

  const { alternatives } = configuration?.publishedTemplate || {};
  const canonicalEnglish =
    (!currentLocale || currentLocale === "en") && alternatives?.length;

  const host = `https://${APIV3.getRequestHost()}`;

  const localeLinks = useMemo(
    () =>
      alternatives?.map(({ locale }) => {
        const isDefault = locale === "en";
        const href = `${host}${isDefault ? "" : "/"}${
          isDefault ? "" : locale
        }${asPath.replace(`${currentLocale}`, "")}`;
        return (
          <link key={locale} rel="alternate" hrefLang={locale} href={href} />
        );
      }),
    [currentLocale, asPath, alternatives, host],
  );
  return (
    <Head>
      {theme.settings.favicon && (
        <link rel="icon" href={theme.settings.favicon} />
      )}
      {hiddenIcons && (
        <style nonce={cache.nonce}>
          {`
            .MuiIcon-root {
              display: none;
            }
          `}
        </style>
      )}
      <title>{`${titlePrefixContent}${titleSufixContent}`}</title>
      {canonicalEnglish && <link rel="canonical" href={host} />}
      {localeLinks}
      <meta name="viewport" content="initial-scale=1, width=device-width" />
      {configuration?.communityToggleSetting.seoDisableIndexing && (
        <meta name="robots" content="noindex, nofollow" />
      )}
      <script
        type="application/ld+json"
        nonce={cache.nonce}
        dangerouslySetInnerHTML={{
          __html: `
          {
            "@context" : "https://schema.org",
            "@type" : "WebSite",
            "name" : "${name}",
            "alternateName": ["${name} Marketplace"],
            "url" : "${domain}"
          }
        `,
        }}
      />
      {googleFontsTag}
      {appFontsTag}
      <link rel="stylesheet" href="/inline-build.css" />
    </Head>
  );
});

export default CommonHead;
